import React, { useMemo } from 'react';
import { useJobs } from 'src/hooks/useJobs';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { IconFileText, IconPlus } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { DrawingDto } from 'src/api/FiretrolApiClient';
import { useDrawingsList } from 'src/api/queries/drawings/useDrawingQueries';
import { useDrawingLogPdf } from 'src/api/queries/drawings/useDrawingMutations';
import { IContact } from 'src/api/models/Organization';
import {
  getErrorMessage,
  logger,
  resolveWorkflowButtonColor,
  showErrorToast,
} from 'src/utils';
import { WorkflowCreateButton } from 'src/app/workflows/WorkflowCreateButton';
import { workflowStatusColumn } from 'src/app/workflows/workflowStatusColumn';
import { WorkflowStatusPanel } from 'src/design/molecules/WorkflowStatusPanel';
import { WorkflowLogModal } from 'src/app/workflows/WorkflowLogModal';
import { useWorkflowStatusFilters } from 'src/app/workflows/useWorkflowStatusFilters';
import { useWorkflowStatusSegments } from 'src/app/workflows/useWorkflowStatusSegments';
import { useWorkflowLogPdf } from 'src/app/workflows/useWorkflowLogPdf';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { usePdfBlobUrl } from 'src/design/molecules/usePdfBlobUrl';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';

const intentLabel = (intent: string | undefined): string =>
  intent === 'info_only' ? 'Information Only' : 'Review and Acceptance';

const getColumns = (): MRT_ColumnDef<DrawingDto>[] => [
  {
    accessorKey: 'drawingNumber',
    header: 'Drawing #',
    size: 150,
  },
  {
    accessorKey: 'title',
    header: 'Title',
    size: 220,
  },
  workflowStatusColumn<DrawingDto>({ size: 160 }),
  {
    accessorKey: 'type',
    header: 'Type',
    size: 140,
  },
  {
    id: 'intent',
    header: 'Intent of Review',
    size: 200,
    accessorFn: (row) => intentLabel(row.intentOfReview),
  },
  {
    id: 'submittedTo',
    header: 'Submitted To',
    size: 200,
    accessorFn: (row) => row.submittedToContact?.name || '',
  },
  {
    id: 'assignee',
    header: 'Assignee',
    size: 180,
    accessorFn: (row) => row.assignee?.fullName || '',
  },
  {
    id: 'daysOpen',
    header: 'Days Open',
    size: 120,
    accessorFn: (row) => (row.daysOpen != null ? String(row.daysOpen) : ''),
  },
];

export const Drawings: React.FC = () => {
  const { selectedJob } = useJobs();
  const { data, isLoading, isFetching, isError, refetch } = useDrawingsList(
    selectedJob?.id,
  );
  const drawings = React.useMemo<DrawingDto[]>(() => data ?? [], [data]);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    covered,
    filters,
    selectableStatuses,
    statusFilter,
    setStatusFilter,
    filteredItems,
  } = useWorkflowStatusFilters(drawings, selectedJob?.drawingWorkflow);
  const segments = useWorkflowStatusSegments(
    drawings,
    filters,
    covered,
    selectedJob?.drawingWorkflow,
  );

  const [logOpen, setLogOpen] = React.useState(false);
  const [logStatuses, setLogStatuses] = React.useState<string[]>([]);
  const [logContact, setLogContact] = React.useState<IContact | null>(null);
  const logPdf = useDrawingLogPdf();
  const { file: logFile, loading: logLoading } = useWorkflowLogPdf({
    open: logOpen,
    jobId: selectedJob?.id,
    statuses: logStatuses,
    contact: logContact,
    fetchPdf: (jobId, statuses, contactId) =>
      logPdf.mutateAsync({ jobId, statuses, contactId }),
    logLabel: 'Failed to load drawing log PDF',
    onError: () => setLogOpen(false),
  });
  const logUrl = usePdfBlobUrl(logFile?.data);

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'drawings',
    columns: columns,
    defaultSorting: [{ id: 'drawingNumber', desc: false }],
  });

  const tableExports = useTableExports<DrawingDto>('drawings', (data) =>
    data.map((item) => ({
      ['Job Name']: selectedJob?.name,
      ['Job Number']: selectedJob?.jobNumber,
      ['Drawing #']: item.drawingNumber,
      ['Title']: item.title,
      ['Status']: item.status,
      ['Type']: item.type,
      ['Intent of Review']: intentLabel(item.intentOfReview),
      ['Submitted To']: item.submittedToContact?.name || '',
      ['Assignee']: item.assignee?.fullName || '',
      ['Days Open']: item.daysOpen != null ? String(item.daysOpen) : '',
    })),
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => (
        <Group>
          <ViewActionIcon
            onClick={() =>
              navigate(
                `/jobs/${row.original.jobId}/drawings/${row.original.id}`,
              )
            }
            label="View Drawing"
          />
        </Group>
      ),
      enableClickToCopy: false,
      columns: columns,
      data: filteredItems,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    getRowId: (row) => String(row.id),
    totalCount: filteredItems.length,
    isLoading,
    isFetching,
    isError,
    onRefresh: () => {
      refetch();
    },
    ...tableExports,
  });

  if (!selectedJob) return null;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(selectedJob, undefined, 'Drawings'),
        }}
        title="Drawings"
        helpText="Manage drawings for this job. Create new drawings, track their review status, and manage document revisions."
        actions={
          selectedJob.drawingWorkflow ? (
            <WorkflowCreateButton
              workflow={selectedJob.drawingWorkflow}
              color={resolveWorkflowButtonColor(
                selectedJob.drawingWorkflow.createButtonColor,
              )}
              leftSection={<IconPlus />}
              onClick={() => navigate(`${location.pathname}/submit/drawing`)}
            >
              {selectedJob.drawingWorkflow.createButtonText || 'Create Drawing'}
            </WorkflowCreateButton>
          ) : undefined
        }
      />

      <Group mb="md">
        <SecondaryButton
          leftSection={<IconFileText />}
          onClick={() => setLogOpen(true)}
        >
          Drawing Log
        </SecondaryButton>
      </Group>

      {!selectedJob.drawingWorkflowId && (
        <Alert mb="md" title="Missing Drawing Workflow" color="red">
          Default workflow does not exist. Please contact an administrator.
        </Alert>
      )}

      <WorkflowStatusPanel
        segments={segments}
        value={statusFilter}
        onChange={setStatusFilter}
      />

      {table}

      <WorkflowLogModal
        opened={logOpen}
        onClose={() => setLogOpen(false)}
        title="Drawing Log"
        pdfTitle="Drawing Log PDF"
        statusOptions={selectableStatuses}
        statuses={logStatuses}
        onStatusChange={setLogStatuses}
        customerNumber={selectedJob.customerNumber?.trim() ?? ''}
        contact={logContact}
        onContactChange={setLogContact}
        logUrl={logUrl}
        logLoading={logLoading}
      />
    </>
  );
};
